import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import U2000BasicDialog from '../../../u2000Core/components/dialog/U2000BasicDialog';
import { FaUndoAlt, FaPlus } from 'react-icons/fa';
import { useToast } from '../../../u2000Core/context/toast/U2000ToastContext';
import { useU3121UploadProjectFromFileHandlers } from './U3121UseUploadProjectFromFileHandlers';
import {
  U3121_UPLOAD_MAX_FILES,
  type U3121UploadFileLineResult,
  u3121AppendUploadFilesToQueue,
  u3121ReadUploadSidecarPreviewText,
  u3121ResolveSelectedUploadFileName,
  u3121ValidateUploadFile,
} from '../../tools/U3121UploadProjectFromFileBatchTools';
import { NS } from '../../U3121';

const cancelButtonClass = `inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg shadow-xs transition-all duration-200
  hover:bg-gray-50 hover:border-gray-400 hover:text-gray-900 hover:shadow-md
  focus:outline-hidden focus:ring-2 focus:ring-gray-500 focus:ring-offset-2
  active:bg-gray-100 active:scale-95
  disabled:cursor-not-allowed disabled:opacity-60`;
const primaryButtonClass = `inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-green-600 rounded-lg shadow-xs transition-all duration-200
  hover:bg-green-700 hover:shadow-md
  focus:outline-hidden focus:ring-2 focus:ring-green-500 focus:ring-offset-2
  active:bg-green-800 active:scale-95
  disabled:cursor-not-allowed disabled:bg-gray-400 disabled:text-gray-200 disabled:opacity-60 disabled:shadow-none`;

type UploadDialogPhase = 'select' | 'progress';

export interface U3121UploadProjectFromFileDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  onBatchComplete?: (results: U3121UploadFileLineResult[]) => void;
}

export const U3121UploadProjectFromFileDialog: React.FC<U3121UploadProjectFromFileDialogProps> = ({
  isOpen,
  onClose,
  onSuccess,
  onBatchComplete,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [phase, setPhase] = useState<UploadDialogPhase>('select');
  const [progressCurrent, setProgressCurrent] = useState(0);
  const [progressTotal, setProgressTotal] = useState(0);
  const [progressFileName, setProgressFileName] = useState('');
  const [formResetKey, setFormResetKey] = useState(0);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const [previewText, setPreviewText] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewErrorKey, setPreviewErrorKey] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dragDepthRef = useRef(0);

  const batchRunning = phase === 'progress';

  const { uploadProjectFromFile, uploadProjectFromFilesSequential } = useU3121UploadProjectFromFileHandlers({
    showErrorToast: false,
    onSuccess: () => {
      setSubmitting(false);
      setInlineError(null);
      setFiles([]);
      onSuccess?.();
      onClose();
    },
    onError: (message) => {
      setSubmitting(false);
      setInlineError(message);
    },
  });

  useEffect(() => {
    if (isOpen) {
      setFiles([]);
      setSubmitting(false);
      setPhase('select');
      setProgressCurrent(0);
      setProgressTotal(0);
      setProgressFileName('');
      setInlineError(null);
      setIsDragActive(false);
      dragDepthRef.current = 0;
      setSelectedFileName(null);
      setPreviewText(null);
      setPreviewLoading(false);
      setPreviewErrorKey(null);
      setFormResetKey((k) => k + 1);
      if (inputRef.current) inputRef.current.value = '';
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || phase !== 'select') {
      return;
    }
    setSelectedFileName((previous) => u3121ResolveSelectedUploadFileName(previous, files));
  }, [files, isOpen, phase]);

  useEffect(() => {
    if (!isOpen || phase !== 'select' || !selectedFileName) {
      setPreviewText(null);
      setPreviewLoading(false);
      setPreviewErrorKey(null);
      return;
    }

    const file = files.find((f) => f.name === selectedFileName);
    if (!file) {
      setPreviewText(null);
      setPreviewLoading(false);
      setPreviewErrorKey(null);
      return;
    }

    let cancelled = false;
    setPreviewLoading(true);
    setPreviewErrorKey(null);
    setPreviewText(null);

    u3121ReadUploadSidecarPreviewText(file)
      .then((text) => {
        if (!cancelled) {
          setPreviewText(text);
          setPreviewLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPreviewErrorKey('msgUploadFilePreviewReadError');
          setPreviewLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedFileName, files, isOpen, phase]);

  const applyIncomingFiles = useCallback(
    (incoming: File[]) => {
      setInlineError(null);
      setFiles((current) => {
        const merged = u3121AppendUploadFilesToQueue(current, incoming);
        if (merged.rejectedOverMax) {
          const isSingleBatchOverMax = incoming.length > U3121_UPLOAD_MAX_FILES;
          setInlineError(
            t(isSingleBatchOverMax ? 'msgUploadTooManyFiles' : 'msgUploadQueueFull', {
              ns: NS,
              max: U3121_UPLOAD_MAX_FILES,
            }),
          );
          return current;
        }
        if (merged.duplicateNames.length > 0) {
          toast.info(t('msgUploadDuplicatesIgnored', { ns: NS, count: merged.duplicateNames.length }));
        }
        return merged.files;
      });
    },
    [t, toast],
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const snapshot = e.target.files ? Array.from(e.target.files) : [];
    if (inputRef.current) inputRef.current.value = '';
    if (snapshot.length > 0) {
      applyIncomingFiles(snapshot);
    }
  };

  const handleRemoveFile = (fileName: string) => {
    setInlineError(null);
    setFiles((current) => current.filter((f) => f.name !== fileName));
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current += 1;
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current -= 1;
    if (dragDepthRef.current <= 0) {
      dragDepthRef.current = 0;
      setIsDragActive(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = 0;
    setIsDragActive(false);
    if (submitting || batchRunning) return;

    const allDropped = Array.from(e.dataTransfer.files);
    const txtFiles = allDropped.filter((f) => f.name.toLowerCase().endsWith('.txt'));
    if (txtFiles.length === 0 && allDropped.length > 0) {
      setInlineError(t('errorBU312105', { ns: NS }));
      return;
    }
    if (txtFiles.length === 0) return;

    applyIncomingFiles(txtFiles);
  };

  const handleClose = () => {
    if (batchRunning || submitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    setInlineError(null);
    if (files.length === 0) {
      setInlineError(t('msgUploadFileRequired', { ns: NS }));
      return;
    }

    if (files.length === 1) {
      const validationKey = u3121ValidateUploadFile(files[0]);
      if (validationKey) {
        setInlineError(t(validationKey, { ns: NS }));
        return;
      }
      setSubmitting(true);
      try {
        const formData = new FormData();
        formData.append('file', files[0]);
        await uploadProjectFromFile(formData);
      } catch {
        /* message via onError */
      }
      return;
    }

    setSubmitting(true);
    setPhase('progress');
    setProgressTotal(files.length);
    setProgressCurrent(0);

    try {
      const results = await uploadProjectFromFilesSequential(files, (current, total, fileName) => {
        setProgressCurrent(current);
        setProgressTotal(total);
        setProgressFileName(fileName);
      });
      setSubmitting(false);
      setPhase('select');
      setFiles([]);
      onClose();
      onBatchComplete?.(results);
    } catch {
      setSubmitting(false);
      setPhase('select');
    }
  };

  const isValid = files.length > 0;
  const progressPercent = progressTotal > 0 ? Math.round((progressCurrent / progressTotal) * 100) : 0;

  if (!isOpen) return null;

  return (
    <U2000BasicDialog
      isOpen={isOpen}
      onClose={() => handleClose()}
      title={t('dlgUploadProjectFromFileTitle', { ns: NS })}
      width="large"
      height="max-h-[45vh]"
    >
      <div className="flex min-h-0 flex-1 flex-col focus:outline-hidden">
        <section className="flex min-h-0 flex-1 flex-col overflow-hidden px-6 py-4">
          {phase === 'progress' ? (
            <div className="flex flex-col gap-4">
              <p className="text-sm text-gray-700">{t('msgUploadBatchInProgress', { ns: NS })}</p>
              <p className="text-sm font-medium text-gray-900">
                {t('lblUploadProgress', { ns: NS, current: progressCurrent, total: progressTotal })}
              </p>
              {progressFileName ? (
                <p className="text-xs text-gray-600 truncate" title={progressFileName}>
                  {progressFileName}
                </p>
              ) : null}
              <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                <div className="h-full bg-green-600 transition-all duration-300" style={{ width: `${progressPercent}%` }} />
              </div>
            </div>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden lg:flex-row lg:gap-6">
              <div className="flex min-h-0 min-w-0 shrink-0 flex-col gap-4 overflow-y-auto lg:max-w-[42%] lg:flex-1">
                {inlineError ? (
                  <div
                    role="alert"
                    className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
                    data-testid="u3121-upload-inline-error"
                  >
                    <p className="font-semibold">{t('errorTitle', { ns: NS })}</p>
                    <p className="mt-1 leading-relaxed">{inlineError}</p>
                  </div>
                ) : null}
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="u3121-upload-project-file">
                    {t('lblUploadProjectFromFile', { ns: NS })}
                  </label>
                  <div className="flex flex-col gap-1">
                    <input
                      id="u3121-upload-project-file"
                      ref={inputRef}
                      key={formResetKey}
                      type="file"
                      accept=".txt"
                      multiple
                      disabled={submitting}
                      onChange={handleFileChange}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:rounded-sm file:border-0 file:bg-gray-100 file:px-4 file:py-2 file:text-sm file:font-medium file:text-gray-700 hover:file:bg-gray-200"
                    />
                    <div
                      role="button"
                      tabIndex={0}
                      data-testid="u3121-upload-drop-zone"
                      onDragEnter={handleDragEnter}
                      onDragLeave={handleDragLeave}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                      className={`mt-2 rounded-lg border-2 border-dashed px-4 py-6 text-center text-sm transition-colors ${
                        isDragActive
                          ? 'border-green-500 bg-green-50 text-green-800'
                          : 'border-gray-300 bg-gray-50 text-gray-600 hover:border-gray-400'
                      } ${submitting ? 'pointer-events-none opacity-60' : ''}`}
                    >
                      {t('msgUploadDropHint', { ns: NS })}
                    </div>
                    <p className="text-xs text-gray-500">{t('msgUploadFileMaxSizeAndCount', { ns: NS, max: U3121_UPLOAD_MAX_FILES })}</p>
                    {files.length > 0 && (
                      <div className="mt-2 max-h-52 overflow-y-auto rounded border border-gray-100 bg-gray-50 p-2">
                        <p className="mb-1 text-xs font-medium text-gray-600">
                          {t('lblUploadSelectedFilesCount', { ns: NS, count: files.length })}
                        </p>
                        <ul className="space-y-1 text-sm text-gray-700" data-testid="u3121-upload-file-queue">
                          {files.map((f) => {
                            const isSelected = f.name === selectedFileName;
                            return (
                              <li
                                key={f.name}
                                data-file-name={f.name}
                                className={`flex items-center justify-between gap-2 rounded px-1 py-0.5 ${
                                  isSelected ? 'bg-green-50 ring-1 ring-green-200' : ''
                                }`}
                              >
                                <button
                                  type="button"
                                  onClick={() => setSelectedFileName(f.name)}
                                  disabled={submitting}
                                  aria-selected={isSelected}
                                  className="min-w-0 flex-1 truncate text-left hover:underline disabled:opacity-60"
                                  aria-label={t('btnSelectFileForPreview', { ns: NS, fileName: f.name })}
                                  title={f.name}
                                >
                                  {f.name}
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveFile(f.name);
                                  }}
                                  disabled={submitting}
                                  className="shrink-0 text-sm text-red-600 hover:underline disabled:opacity-60"
                                  aria-label={t('btnRemoveFileFromQueue', { ns: NS, fileName: f.name })}
                                >
                                  ({t('btnRemove', { ns: NS })})
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div
                className="flex min-h-[min(140px,20vh)] min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white lg:min-h-0 lg:max-h-[calc(45vh-9rem)]"
                data-testid="u3121-upload-file-preview-panel"
              >
                <p className="shrink-0 border-b border-gray-100 px-3 py-2 text-sm font-medium text-gray-800">
                  {selectedFileName
                    ? t('lblUploadFilePreviewFor', { ns: NS, fileName: selectedFileName })
                    : t('lblUploadFilePreview', { ns: NS })}
                </p>
                <div
                  className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-3"
                  data-testid="u3121-upload-file-preview-scroll"
                >
                  {!selectedFileName || files.length === 0 ? (
                    <p className="text-sm text-gray-500" data-testid="u3121-upload-file-preview-empty">
                      {t('msgUploadFilePreviewEmpty', { ns: NS })}
                    </p>
                  ) : previewLoading ? (
                    <p className="text-sm text-gray-600">{t('msgUploadFilePreviewLoading', { ns: NS })}</p>
                  ) : previewErrorKey ? (
                    <p className="text-sm text-gray-700">{t(previewErrorKey, { ns: NS })}</p>
                  ) : (
                    <pre
                      className="whitespace-pre-wrap break-words font-mono text-xs leading-relaxed text-gray-800"
                      data-testid="u3121-upload-file-preview"
                    >
                      {previewText ?? ''}
                    </pre>
                  )}
                </div>
              </div>
            </div>
          )}
        </section>

        <footer className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-2xl shrink-0">
          <button
            type="button"
            className={primaryButtonClass}
            onClick={() => handleSubmit()}
            disabled={!isValid || submitting}
            aria-label={t('btnUploadProjectFromFile', { ns: NS })}
          >
            <FaPlus className="w-4 h-4" />
            <span>{files.length > 1 ? t('btnImportProjects', { ns: NS }) : t('btnCreateProject', { ns: NS })}</span>
          </button>
          <button
            type="button"
            className={cancelButtonClass}
            onClick={() => handleClose()}
            disabled={submitting}
            aria-label={t('btnCancel', { ns: NS })}
          >
            <FaUndoAlt className="w-4 h-4" />
            <span>{t('btnCancel', { ns: NS })}</span>
          </button>
        </footer>
      </div>
    </U2000BasicDialog>
  );
};
